import { useState, useEffect, useMemo, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, CircleHelp } from "lucide-react";
import type { VanityLink, VanityLinkWrite } from "../../api/_lib/types";
import { UTM_TERM_PARAM_DEFAULT, isValidParamName } from "../../api/_lib/redirect-dest";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type LinkData = VanityLinkWrite;

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: LinkData) => Promise<void>;
  initial?: VanityLink | null;
}

const EMPTY: LinkData = {
  slug: "",
  url: "",
  utmSource: "",
  utmMedium: "",
  utmCampaign: "",
  utmContent: "",
  clickIdEnabled: false,
};

function LabelWithHint({
  htmlFor,
  hint,
  className,
  children,
}: {
  htmlFor: string;
  hint: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor} className={className}>
        {children}
      </Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex text-muted-foreground hover:text-foreground rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 shrink-0"
            aria-label={typeof children === "string" ? `Help: ${children}` : "Field help"}
          >
            <CircleHelp className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[280px] leading-snug">
          {hint}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function suggestSlug(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.hostname.replace(/^www\./, "").split(".");
    return parts[0] ?? "";
  } catch {
    return "";
  }
}

export function LinkForm({ open, onClose, onSave, initial }: Props) {
  const [form, setForm] = useState<LinkData>(EMPTY);
  const [showUtm, setShowUtm] = useState(false);
  const [customParam, setCustomParam] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial } : EMPTY);
      setShowUtm(!!(initial?.utmSource || initial?.utmMedium || initial?.utmCampaign || initial?.utmContent));
      setCustomParam(!!initial?.utmTermParam);
      setError(null);
    }
  }, [open, initial]);

  const set = (field: keyof LinkData, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const termParam = (customParam && form.utmTermParam?.trim()) || UTM_TERM_PARAM_DEFAULT;

  const handleUrlBlur = () => {
    if (!form.slug && form.url) {
      set("slug", suggestSlug(form.url));
    }
  };

  const shortUrl = useMemo(() => {
    if (!form.slug) return "";
    return `${window.location.host}/${form.slug}`;
  }, [form.slug]);

  const handleSave = async () => {
    if (!form.slug || !form.url) {
      setError("Slug and destination URL are required.");
      return;
    }
    const customName = form.utmTermParam?.trim() ?? "";
    if (customParam && !isValidParamName(customName)) {
      setError("Param name must be alphanumeric with hyphens/underscores.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Strip empty UTM fields; the beacon value is set on redirect only
      const data: LinkData = { ...form };
      delete data.utmTerm;
      if (!data.utmSource) delete data.utmSource;
      if (!data.utmMedium) delete data.utmMedium;
      if (!data.utmCampaign) delete data.utmCampaign;
      if (!data.utmContent) delete data.utmContent;
      // null clears a previously stored override on edit
      data.utmTermParam = customParam ? customName : null;
      await onSave(data);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <TooltipProvider delayDuration={200}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit link" : "Create link"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <LabelWithHint
              htmlFor="url"
              hint="The page people land on after clicking the short link. Include https:// and path; add fixed query params here if the destination always needs them."
            >
              Destination URL
            </LabelWithHint>
            <Input
              id="url"
              placeholder="https://example.com/page"
              value={form.url}
              onChange={e => set("url", e.target.value)}
              onBlur={handleUrlBlur}
            />
          </div>

          <div className="space-y-1.5">
            <LabelWithHint
              htmlFor="slug"
              hint="The path of your short link (e.g. reddit → …/reddit). Keep it short and memorable. Letters, numbers, hyphens, and underscores only."
            >
              Slug
            </LabelWithHint>
            <div className="flex items-center">
              <span className="text-xs text-muted-foreground mr-1.5 whitespace-nowrap">l.circlesup.com/</span>
              <Input
                id="slug"
                placeholder="reddit"
                value={form.slug}
                onChange={e => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ""))}
                disabled={isEdit}
                className={isEdit ? "opacity-60" : ""}
              />
            </div>
          </div>

          <button
            type="button"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowUtm(v => !v)}
          >
            {showUtm ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            UTM parameters
          </button>

          {showUtm && (
            <div className="space-y-3 pl-2 border-l border-border">
              {(
                [
                  ["utmSource", "Who or what sent the traffic. Typical values: reddit, google, newsletter, linkedin, partner name."],
                  ["utmCampaign", "Campaign or program name so you can group results in reports, e.g. spring-sale, onboarding, q1-webinar."],
                  ["utmMedium", "Channel type: social, email, cpc (paid search), display, referral, organic."],
                  ["utmContent", "Differentiates multiple links or creatives in the same campaign, e.g. hero-button, footer, banner-a vs banner-b."],
                ] as const
              ).map(([field, hint]) => (
                <div key={field} className="space-y-1.5">
                  <LabelWithHint
                    htmlFor={field}
                    className="text-xs"
                    hint={hint}
                  >
                    {field.replace(/^utm([A-Z])/, (_, c: string) => `utm_${c.toLowerCase()}`)}
                  </LabelWithHint>
                  <Input
                    id={field}
                    placeholder={field.replace(/^utm/, "").toLowerCase()}
                    value={form[field] ?? ""}
                    onChange={e => set(field, e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <LabelWithHint
              htmlFor="utm_term_preview"
              className="text-xs"
              hint="Set on redirect to host + slug only (no https://), e.g. l.example.com/reddit. Read it in analytics (e.g. Mixpanel) to see which vanity link was clicked."
            >
              {termParam}
            </LabelWithHint>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Short host path on redirect (no protocol). Value not editable.
            </p>
            <Input
              id="utm_term_preview"
              readOnly
              tabIndex={-1}
              value={shortUrl}
              placeholder={form.slug ? undefined : "Set a slug to preview"}
              className="h-8 text-sm bg-muted/50"
            />

            <label className="flex items-center gap-2 pt-1 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={customParam}
                onChange={e => {
                  setCustomParam(e.target.checked);
                  if (!e.target.checked) set("utmTermParam", "");
                }}
                className="h-3.5 w-3.5 accent-foreground"
              />
              Use a custom param name
            </label>

            {customParam && (
              <div className="space-y-1.5 pt-0.5">
                <Input
                  id="utmTermParam"
                  placeholder={UTM_TERM_PARAM_DEFAULT}
                  value={form.utmTermParam ?? ""}
                  onChange={e => set("utmTermParam", e.target.value)}
                  className="h-8 text-sm"
                />
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Some destinations treat any <code>utm_*</code> param as marketing traffic and reroute
                  the visit (e.g. into a signup funnel). A non-UTM name keeps the link landing where you
                  pointed it, while still colouring the visit.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.clickIdEnabled}
                onChange={e => setForm(prev => ({ ...prev, clickIdEnabled: e.target.checked }))}
                className="h-3.5 w-3.5 accent-foreground"
              />
              Add click id (clickid)
            </label>
            <p className="text-[11px] text-muted-foreground leading-snug pl-6">
              Appends a fresh unique <code>clickid</code> value to the destination on every redirect,
              like Meta's <code>fbclid</code> — use it to key downstream records (e.g. quiz responses) to this click.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create link"}
          </Button>
        </DialogFooter>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
